import {
  Checkbox,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
} from '@monksflow/monks-ui';
import { format } from 'date-fns';
import { Loader2Icon, PlayIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { adUsageLabelKey } from '../lib/ad-usage';
import type { MediaTile } from '../model/media-tile';

/**
 * The same media as `MediaGrid`, read as rows rather than looked at as pictures.
 *
 * What a list answers that a wall of thumbnails cannot: which of these is a clip, how each one was
 * classified, and when it arrived. The thumbnail stays, small, because a title alone does not
 * identify a photograph.
 *
 * Selection is the grid's, unchanged -- the same `selectedIds` keyed by media id, ticked through the
 * same checkbox, and the same click on the body to open -- so switching view cannot change what is
 * selected or what the detail panel is showing. The checkbox has to be here rather than only in the
 * grid: without a column for it there is no way to tell, in this view, what a bulk action is about
 * to touch.
 */
export const MediaTable = ({
  tiles,
  selectedIds,
  openMediaId,
  onChoose,
  onToggleSelect,
}: {
  tiles: readonly MediaTile[];
  selectedIds: ReadonlySet<string>;
  /** The row the detail panel is describing, which the table tints with its own `active`. */
  openMediaId?: string;
  onChoose: (assetId: string) => void;
  onToggleSelect: (mediaId: string) => void;
}) => {
  const { t } = useTranslation('tools');

  // Nothing rather than a header over no rows: the host already says why the library is empty, and a
  // lone row of column names would read as a table that failed to load.
  if (tiles.length === 0) return null;

  // Truncating rather than nowrap alone: a fixed layout with an unclipped cell lets a long value
  // spill past the table, which paints a selected row's background over whatever is beside it.
  const cell = 'truncate px-2 py-2 text-sm';
  const head = 'px-2 py-2 text-xs text-muted-foreground';

  return (
    /* `Datatable` puts the table inside a horizontal scroll container of its own, and a scroll
       container used as a flex item has an automatic minimum size of zero rather than its content.
       Dropped straight into a column that scrolls -- which is where a library of any size ends up --
       it is squeezed to no height at all and the rows vanish while staying in the DOM. The wrapper
       is what stops the squeeze; the table itself keeps its own width behaviour. */
    <div className="shrink-0" data-testid="media-table">
      <Datatable className="w-full table-fixed">
        <DatatableHeader>
          <DatatableRow>
            {/* The widths live here because a fixed layout takes them from the first row. The date is
              the widest of the three narrow columns, so it gets the most of what the name leaves. */}
            <DatatableHead className={`w-11 ${head}`}>
              {/* A column of checkboxes needs a name for a screen reader reading the header row, and
                a printed "Select" over four ticks is a word doing nothing for anyone looking. */}
              <span className="sr-only">{t('brandMedia.columns.select')}</span>
            </DatatableHead>
            <DatatableHead className={`w-[42%] ${head}`}>
              {t('brandMedia.columns.name')}
            </DatatableHead>
            <DatatableHead className={`w-[14%] ${head}`}>
              {t('brandMedia.columns.type')}
            </DatatableHead>
            <DatatableHead className={`w-[16%] ${head}`}>
              {t('brandMedia.columns.usage')}
            </DatatableHead>
            <DatatableHead className={`w-[24%] ${head}`}>
              {t('brandMedia.columns.added')}
            </DatatableHead>
          </DatatableRow>
        </DatatableHeader>

        <DatatableBody>
          {tiles.map((tile) => {
            const selected = selectedIds.has(tile.id);
            const isVideo = tile.mediaType === 'video';
            const usageKey = adUsageLabelKey(tile.tags);

            return (
              <DatatableRow
                key={tile.id}
                interactive
                // Both tints are the table's own, and the open row takes precedence over a ticked
                // one: its checkbox still says it is in the selection, while nothing else in the
                // row would say the panel is describing it.
                state={tile.id === openMediaId ? 'active' : selected ? 'selected' : 'default'}
                className="cursor-pointer"
                // The mouse affordance is the whole row, the way a file list behaves everywhere else.
                // The button in the name cell is what a keyboard and a screen reader get, and it stops
                // its own click here so one click is not counted twice.
                onClick={() => onChoose(tile.assetId)}
              >
                <DatatableCell className={`${cell} pl-1`}>
                  <Checkbox
                    aria-label={t('brandMedia.selectAsset', { title: tile.title })}
                    checked={selected}
                    // Ticking is the one thing in this row that does not open the asset, so the
                    // click stops here rather than reaching the row behind it. Space and Enter on a
                    // focused checkbox dispatch a click too, so this covers the keyboard as well.
                    onClick={(event) => event.stopPropagation()}
                    onCheckedChange={() => onToggleSelect(tile.id)}
                  />
                </DatatableCell>

                <DatatableCell className={cell}>
                  <button
                    type="button"
                    // The row's tint says the same thing to anyone looking at the list.
                    aria-current={tile.id === openMediaId ? 'true' : undefined}
                    {...(isVideo
                      ? { 'aria-label': t('brandMedia.videoTile', { title: tile.title }) }
                      : {})}
                    title={tile.description ?? tile.title}
                    className="flex w-full min-w-0 items-center gap-2 text-left"
                    onClick={(event) => {
                      event.stopPropagation();
                      onChoose(tile.assetId);
                    }}
                  >
                    <span className="relative flex size-8 shrink-0 overflow-hidden rounded border border-border bg-transparency-checker">
                      {/* No element until the url resolves: an empty src is a request for the current
                        page, not an absent image. */}
                      {tile.url !== undefined && (
                        <img src={tile.url} alt="" className="size-full object-cover" />
                      )}

                      {/* The type column names it too; this is what makes a clip findable while
                        scanning the thumbnails rather than the words. */}
                      {isVideo && (
                        <span
                          data-testid="media-tile-video-badge"
                          aria-hidden="true"
                          className="absolute inset-0 flex items-center justify-center bg-black/40 text-white"
                        >
                          <PlayIcon className="size-3" />
                        </span>
                      )}
                    </span>

                    <span className="truncate">{tile.title}</span>

                    {/* Beside the title rather than on the thumbnail, which at this size already
                      carries the video badge. */}
                    {tile.ingestion === 'pending' && (
                      <Loader2Icon
                        data-testid="media-tile-ingesting"
                        aria-hidden="true"
                        className="size-3 shrink-0 animate-spin text-muted-foreground"
                      />
                    )}
                  </button>
                </DatatableCell>

                <DatatableCell className={cell} emphasis="subtle">
                  {isVideo ? t('brandMedia.type.video') : t('brandMedia.type.image')}
                </DatatableCell>

                <DatatableCell className={cell} emphasis="subtle">
                  {usageKey === undefined ? '-' : t(usageKey)}
                </DatatableCell>

                <DatatableCell className={cell} emphasis="subtle">
                  {tile.createdAt === undefined
                    ? '-'
                    : // eslint-disable-next-line i18next/no-literal-string -- a date-fns format pattern, not copy
                      format(new Date(tile.createdAt), 'd MMM yyyy')}
                </DatatableCell>
              </DatatableRow>
            );
          })}
        </DatatableBody>
      </Datatable>
    </div>
  );
};
